/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : GeneralInfoSlice.ts
 *
 * Project Title :  APM Settings Tool
 *
 * Description   : Redux Slice for New Config components state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Jan 03, 2022
 *
 * @Version      : Version 1.0
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IGeneralInfoFormFields, IGeneralInfoState } from "models/generalInfo";
import { AppThunk, RootState } from "stores/GlobalStore";
import { setSelectedApmRecordData } from "stores/ApmWorkFlowStore";
import { apiCallBegan, apiVersion, apmURLs } from "api";
import { IUserContactDetails } from "models/auth";

const initialState: IGeneralInfoState = {
  formFieldsData: {
    customerName: "",
    customerPhone: "",
    customerEmail: "",
  },
  loggedInUserDetailsLoader: false,
  loggedInUserDetails: {
    userId: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
  },
};

const generalInfoSlice = createSlice({
  name: "generalInfo",
  initialState,
  reducers: {
    setGeneralInfo(
      generalInfoState: IGeneralInfoState,
      action: PayloadAction<IGeneralInfoFormFields>
    ) {
      generalInfoState.formFieldsData = action.payload;
    },
    setLoggedInUserContactDetails(
      generalInfoState: IGeneralInfoState,
      action: PayloadAction<IUserContactDetails>
    ) {
      generalInfoState.loggedInUserDetails = action.payload;
      generalInfoState.loggedInUserDetailsLoader = false;
    },
    beginFetchUserContactDetailsLoader(generalInfoState: IGeneralInfoState) {
      generalInfoState.loggedInUserDetailsLoader = true;
    },
    resetUserContactDetailsOnError(generalInfoState: IGeneralInfoState) {
      generalInfoState.loggedInUserDetails = null;
      generalInfoState.loggedInUserDetailsLoader = false;
    },
    resetGeneralInfoForm(generalInfoState: IGeneralInfoState) {
      generalInfoState.formFieldsData = initialState.formFieldsData;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      setSelectedApmRecordData,
      (generalInfoState: IGeneralInfoState, action) => {
        generalInfoState.formFieldsData.customerName =
          action.payload.customerName;
        generalInfoState.formFieldsData.customerEmail =
          action.payload.customerEmail;
        generalInfoState.formFieldsData.customerPhone =
          action.payload.customerPhoneNo;
      }
    );
  },
});

export const {
  setGeneralInfo,
  setLoggedInUserContactDetails,
  beginFetchUserContactDetailsLoader,
  resetUserContactDetailsOnError,
  resetGeneralInfoForm,
} = generalInfoSlice.actions;
export const generalInfoReducer = generalInfoSlice.reducer;

export const fetchLoggedInUserContactDetails =
  (): AppThunk => async (dispatch) => {
    const url = apiVersion.v1 + apmURLs.USER_CONTACT_DETAILS;

    dispatch(
      apiCallBegan({
        url,
        onStart: beginFetchUserContactDetailsLoader.type,
        onSuccess: setLoggedInUserContactDetails.type,
        onError: resetUserContactDetailsOnError.type,
        showGlobalError: true,
      })
    );
  };

export const selectGeneralInfoFormData = (
  state: RootState
): IGeneralInfoFormFields => state.generalInfo.formFieldsData;
export const selectLoggedInUserContactDetails = (
  state: RootState
): IUserContactDetails | null => state.generalInfo.loggedInUserDetails;
export const selectLoggedInUserContactDetailsLoader = (
  state: RootState
): boolean => state.generalInfo.loggedInUserDetailsLoader;
